import React, { useState, useEffect } from 'react';
import { NumberWithStaticUnitsInput, Tooltip } from 'components/common';

// A number and its unit entered in ONE row, for the case where the unit is a short fixed list rather
// than a value the user types: the count and the unit that count is measured in.
//
// The units are whatever `unit_values` lists. Nothing here is specific to any of them:
//
//   form_type: delayed_integer_with_static_units
//   unit_name: wastage_unit                              # sibling attribute the choice is written to
//   unit_values: [sheet, product]                        # ANY values -- [mm, in] works the same way
//   unit_label: quote_builder.wastage_unit               # optional, names the picker for screen readers
//   unit_translate_prefix: 'quote_builder.wastage_unit_' # optional; absent means the values ARE the labels
//   unit_publish_item_key: quote_builder.…_changed       # optional, published when the unit changes
//
// The unit itself lives in the sibling attribute named by `unit_name`, which is what the form posts;
// this field only decides where it is entered. `delayed_integer_with_unit` is untouched and still
// renders the unit as a read-only label taken from another attribute.
const DelayedNumberWithStaticUnitsFormField = ({ config, value, values, setValue, register, errors, readOnly, disabled, integerOnly, requestPublishItemOnChange, navSection }) => {
  const [inputValue, setInputValue] = useState(value);
  const unit = values[config.unit_name];

  // A field-level read-only expression evaluated against the current form `values` (e.g. the wastage
  // count turns read-only while "otomatik fire" is ticked). Re-evaluated on every render, so it tracks
  // the field it depends on the same way visible_if does -- but the scope here is narrower than
  // visible_if's: only `values` and `window` are in scope (not navSection/options/item), so a
  // readonly_if expression must confine itself to those. When a parent passes a section-wide `readOnly`
  // it still forces read-only (the v1 FormFieldFactory does; the Frank/v2 form does not pass one).
  const configReadOnly = config.readonly_if != undefined ? Boolean(eval(config.readonly_if)) : false;
  const effectiveReadOnly = readOnly || configReadOnly;

  // When config.auto_field names a boolean sibling (the section's wastage_count_auto), this field carries an
  // inline auto/manual toggle in place of a separate checkbox row: automatic shows the computed value read-only
  // with a tooltip and an Edit button that hands control back to the user; manual shows the input with a button
  // to return to automatic. Frank (version 2) only -- the automatic computation lives in the Frank solve.
  const hasAutoToggle = config.auto_field != undefined &&
    typeof window !== 'undefined' && window.user_quote && window.user_quote.version == 2;
  const isAuto = hasAutoToggle && Boolean(values[config.auto_field]);

  const setAuto = (nextAuto) => {
    setValue(config.auto_field, nextAuto);
    // The form posts when updated_at changes, so toggling auto has to touch it the way choosing a unit does.
    setValue('updated_at', Date.now());
  }

  // Pricing a cost section recomputes THAT section's automatic fire adeti on the server (returned by the section
  // calculator and re-emitted by QuoteBuilderForm via emitEvent). While the field is in automatic mode -- read-only,
  // so nothing the user typed is at stake -- it takes the value ADDRESSED TO ITS OWN SECTION, so the count updates
  // without a reload. The event is a document-level broadcast and every nav section mounts its own field, so the
  // label filter is what stops re-pricing one section from overwriting the others (the same filter
  // total_price_in_pence_set uses in QuoteBuilderNavSection). isAuto already requires the v2 auto toggle, so a
  // manual field and every non-wastage field ignore it.
  useEffect(() => {
    const onRecomputed = (event) => {
      if(event.detail.label != navSection?.label) { return; }
      if(!isAuto) { return; }

      setInputValue(event.detail.wastage_count);
      setValue(config.name, event.detail.wastage_count);
    };
    document.addEventListener('nav_section_wastage_recomputed', onRecomputed);
    return () => { document.removeEventListener('nav_section_wastage_recomputed', onRecomputed); };
  }, [isAuto, navSection?.label]);

  const handleDelayedInputValue = (value) => {
    setValue(config.name, value);
    if(config.publish_on_change_key != undefined) {
      var event = new CustomEvent(config.publish_on_change_key, { "detail": value });
      document.dispatchEvent(event);
    }

    if((config.publish_item_on_change && config.publish_item_if_eval == undefined) || (config.publish_item_on_change && eval(config.publish_item_if_eval)) ) {
      requestPublishItemOnChange({ publish_item_key: config.publish_item_key });
    }
  }

  useEffect(() => {
    const timeOutId = setTimeout(() => handleDelayedInputValue(inputValue), 500);
    return () => clearTimeout(timeOutId);
  }, [inputValue]);

  // No naming convention is assumed: with a prefix the label is looked up, without one the value is
  // shown as it stands, so a list of already-readable units needs no translation keys at all.
  const unitLabel = (unitValue) => {
    return config.unit_translate_prefix != undefined ? t(config.unit_translate_prefix + unitValue) : unitValue;
  }

  const unitOptions = config.unit_values.map((unitValue) => {
    return { value: unitValue, label: unitLabel(unitValue) };
  });

  const handleUnitChange = (event) => {
    setValue(config.unit_name, event.target.value);
    // The form posts when updated_at changes, so choosing a unit has to touch it the way a select does.
    setValue('updated_at', Date.now());
  }

  // Queued on mount as well as on change, the way SelectFormField publishes, so subscribers still
  // receive the unit with the form's first post.
  useEffect(() => {
    if(config.unit_publish_item_key != undefined) {
      requestPublishItemOnChange({ publish_item_key: config.unit_publish_item_key });
    }
  }, [unit]);

  const inputElement = (
    <NumberWithStaticUnitsInput
      name={config.name}
      placeholder={config.placeholder}
      register={register}
      errors={errors}
      class_name={config.class_name}
      integerOnly={integerOnly}
      unitName={config.unit_name}
      unitValue={unit}
      unitOptions={unitOptions}
      unitAriaLabel={config.unit_label != undefined ? t(config.unit_label) : undefined}
      onUnitChange={handleUnitChange}
      unitDisabled={effectiveReadOnly || disabled}
      validators={config.validators != undefined ? config.validators : []}
      value={inputValue}
      onChange={event => setInputValue(event.target.value)}
      readOnly={effectiveReadOnly}
    />
  );

  return (
    <tr>
      <td>
        {t(config.label)} { config.tooltip_text != undefined && <Tooltip title={t(config.tooltip_text)} /> }
        {config.helper_text != undefined && (
          <><br /><small className="form-text text-muted" dangerouslySetInnerHTML={{ __html: t(config.helper_text) }} /></>
        )}
      </td>
      <td align="right">
        {hasAutoToggle ? (
          // Mirrors the app's select + edit-link rows (e.g. the material picker): a 265px box, right-aligned like
          // the .general-frm-element selects above and below, so the input's left edge lines up with them; the
          // input fills the box and the auto/manual toggle is pinned to the far right where every Duzenle sits.
          <div style={{ display: 'inline-flex', alignItems: 'center', width: '265px' }}>
            <div className="wastage-auto-value" style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'left' }}>{inputElement}</div>
            {isAuto ? (
              <>
                {/* The ? explains the input's auto value, so it hugs the input; Duzenle is set apart to the right. */}
                <span style={{ flexShrink: 0, marginLeft: '6px' }}><Tooltip title={t(config.auto_tooltip)} /></span>
                <a href="#" className="qb-attribute-edit-link" style={{ flexShrink: 0, whiteSpace: 'nowrap', marginLeft: '16px' }}
                   onClick={(event) => { event.preventDefault(); setAuto(false); }}>
                  <i className="fa fa-pencil-square-o" aria-hidden="true"></i> {t('quote_builder.edit')}
                </a>
              </>
            ) : (
              <a href="#" className="qb-attribute-edit-link" style={{ flexShrink: 0, whiteSpace: 'nowrap', marginLeft: '16px' }}
                 onClick={(event) => { event.preventDefault(); setAuto(true); }}>
                <i className="fa fa-magic" aria-hidden="true"></i> {t(config.auto_label)}
              </a>
            )}
          </div>
        ) : inputElement}
      </td>
    </tr>
  )
}

export default DelayedNumberWithStaticUnitsFormField;
